import { useEffect, useMemo, useRef, useState } from 'react';
import { api } from '../../api/client';
import { useNotify } from '../../components/notifications';
import { LoaderSymbol } from '../../components/loading';
import { useUnsavedGuard } from '../../hooks/useUnsavedGuard';
import { useNavActions } from '../../hooks/useNavActions';
import { FormTitle } from '../../components/FormTitle';
import { designerFrameSrc, designerOrigin } from './reportDesignerUrl';

// ─────────────────────────────────────────────────────────────────────────────
// ReportBuilderPage — Settings ▸ General ▸ Report Builder (legacy XAF "Report
// Builder" nav → ReportDataV2_ListView, the DevExpress ReportsV2 web report
// designer). Rather than a data form, this hosts the SAME DevExpress v20.1 Web
// Report Designer the app already runs for the Custom Report Builder — served by
// src/AlpacaIntelligence.ReportDesigner behind the API's /reports/* forwarder.
//
// It opens the designer on a NEW blank report ({origin}/reports/designer?rds={token}).
// `rds` is a Report-scoped designer-session capability token (ADR-0014) — an iframe
// cannot send the SPA's JWT, so the token is minted here and sent once; the forwarder
// turns it into a cookie for the designer's own follow-up requests. The token is the
// generic report-designer session (POST …/custom-report-templates/designer-session,
// DesignerScope.Report) shared by every report-design surface.
//
// Unsaved-changes guard (standing rule 15): the designer iframe posts its dirty state
// ({type:'alpaca-rd:state', dirty}); useUnsavedGuard then makes sidebar navigation /
// Sign Out show the shared "Unsaved changes will be lost" confirm.
// ─────────────────────────────────────────────────────────────────────────────

interface DesignerSession { token: string; idleSeconds: number; }

function apiErrMsg(e: unknown, fallback: string): string {
  return (e as { response?: { data?: { error?: { message?: string } } } })?.response?.data?.error?.message
    ?? (e instanceof Error ? e.message : fallback);
}

export default function ReportBuilderPage() {
  const notify = useNotify();
  // MAC rights: the whole page is a report DESIGNER (it creates and saves reports), so a role
  // without Write is not given a designer session — it sees a read-only notice instead.
  const perm = useNavActions('set-report-builder');
  const canDesign = perm.write;
  const [session, setSession] = useState<DesignerSession | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [dirty, setDirty] = useState(false);
  const startRef = useRef(performance.now());
  const [loadMs, setLoadMs] = useState<number | null>(null);

  // ── Report-designer session (the DevExpress iframe needs it) ────────────────
  useEffect(() => {
    if (!canDesign) { setLoadMs(performance.now() - startRef.current); return; }
    let cancelled = false;
    setSession(null); setError(null);
    (async () => {
      try {
        const res = await api.post('/api/v1/custom-report-templates/designer-session');
        if (!cancelled) { setSession((res.data as { data: DesignerSession }).data); setLoadMs(performance.now() - startRef.current); }
      } catch (e) {
        const msg = apiErrMsg(e, 'API error.');
        if (!cancelled) { setError(msg); setLoadMs(performance.now() - startRef.current); notify.error(`Could not open the report designer: ${msg}`); }
      }
    })();
    return () => { cancelled = true; };
  }, [notify, canDesign]);

  // ── Dirty state from the design iframe (postMessage bridge) ─────────────────
  useEffect(() => {
    const origin = designerOrigin();
    const onMessage = (ev: MessageEvent) => {
      if (ev.origin !== origin) return;
      const data = ev.data as { type?: string; dirty?: boolean } | null;
      if (data?.type === 'alpaca-rd:state') setDirty(!!data.dirty);
    };
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, []);
  useUnsavedGuard(dirty);

  // A missing report name opens the designer's own "New report" flow.
  const src = useMemo(() => (session ? designerFrameSrc('design', null, session.token) : null), [session]);

  return (
    <div className="rb-page">
      <FormTitle pageId="set-report-builder" loadMs={loadMs} loading={canDesign && !session && !error} />
      <div className="rb-frame">
        {!canDesign ? (
          <div className="rb-msg"><i className="ti ti-lock" /> You have read-only access to Report Builder, so the report designer is not available.</div>
        ) : error ? (
          <div className="rb-msg rb-msg-err"><i className="ti ti-alert-triangle" /> {error}</div>
        ) : !src ? (
          <div className="rb-msg"><LoaderSymbol size={28} /> Opening the report designer…</div>
        ) : (
          <iframe key={src} title="Report Builder — Report Designer" src={src} className="rb-iframe" />
        )}
      </div>
      <style>{styles}</style>
    </div>
  );
}

const styles = `
  .rb-page { display:flex; flex-direction:column; flex:1 1 auto; min-height:0; }
  .rb-frame { flex:1 1 auto; min-height:0; display:flex; margin-top:8px;
    border:1px solid var(--ai-border, #e3e6ec); border-radius:8px; overflow:hidden; background:var(--ai-card, #fff); }
  .rb-iframe { flex:1 1 auto; width:100%; height:100%; border:0; }
  .rb-msg { flex:1 1 auto; display:flex; align-items:center; justify-content:center; gap:10px;
    color:var(--ai-muted, #6b7280); font-size:14px; }
  .rb-msg-err { color:var(--ai-red, #d64545); }
`;
